import { useEffect, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { ErrorState } from "@/components/ui/States";
import { useAction, useItem } from "@/lib/api/queries";
import { relative, dateTime } from "@/lib/format";

/**
 * 1C web-publikatsiyasining Basic hisobi.
 *
 * Nega panelda: 1C tomonida parol almashsa almashinuv DARHOL
 * to'xtaydi. `.env` ni tahrirlash serverga kirishni va servisni
 * qayta ko'tarishni talab qilardi — ya'ni nosozlikni tuzatish
 * uchun har safar ijrochi kerak bo'lardi.
 *
 * Parol QAYTA KO'RSATILMAYDI: serverdan faqat niqob keladi.
 * Bo'sh qoldirilsa eskisi saqlanadi.
 */
interface OnecAuth {
  username: string;
  password_mask: string;
  password_set: boolean;
  configured: boolean;
  encrypted: boolean;
  from_env: boolean;
  updated_at?: string;
  version: number;
}

export function OnecAuthPanel({ canEdit }: { canEdit: boolean }) {
  const state = useItem<OnecAuth>("integration", "/api/v1/integration/onec-auth");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tested, setTested] = useState<string | null>(null);

  const loaded = state.data;
  useEffect(() => {
    if (loaded) setUsername(loaded.username);
  }, [loaded]);

  const save = useAction<OnecAuth, void>("integration", () => ({
    path: "/api/v1/integration/onec-auth",
    method: "PUT",
    body: {
      username: username.trim(),
      // `null` — «tegilmadi»; bo'sh satr yuborilsa parol tozalanardi.
      password: password === "" ? null : password,
    },
  }));
  const test = useAction<{ status: number }, void>("integration", () => ({
    path: "/api/v1/integration/onec-auth/test",
    method: "POST",
  }));

  const data = state.data;

  return (
    <section className="section">
      <div className="section-title">1C hisobi (Basic)</div>
      <div className="group group-card">
        {state.error && <ErrorState error={state.error} onRetry={() => void state.refetch()} />}

        {data && !data.encrypted && (
          <div className="notice">
            <Icon name="alert" size={16} />
            <span>
              Serverda shifrlash kaliti yo'q (<code>INTEGRATION_SECRET_KEY</code>) — hisob
              saqlanmaydi. Parol bazaga ochiq tushmasligi uchun ataylab shunday.
            </span>
          </div>
        )}
        {data?.from_env && (
          <div className="notice">
            <Icon name="alert" size={16} />
            <span>
              Hozir <code>.env</code> dagi eski hisob ishlayapti. Bu yerga kiritsangiz
              u darhol ishlatiladi va keyin parolni serverga kirmasdan almashtirasiz.
            </span>
          </div>
        )}

        <div className="grid-2">
          <label className="field">
            <span className="field-label">Login</span>
            <input
              className="input mono"
              autoComplete="off"
              placeholder="WMS Integratsiya"
              value={username}
              disabled={!canEdit}
              onChange={(event) => {
                setTested(null);
                setUsername(event.target.value);
              }}
            />
            <span className="field-hint">
              1C'da ALOHIDA foydalanuvchi: faqat WMS HTTP-servisiga huquqli,
              administrator emas.
            </span>
          </label>
          <label className="field">
            <span className="field-label">Parol</span>
            <input
              className="input mono"
              type="password"
              autoComplete="off"
              placeholder={data?.password_set ? data.password_mask : "1C foydalanuvchisi paroli"}
              value={password}
              disabled={!canEdit}
              onChange={(event) => {
                setTested(null);
                setPassword(event.target.value);
              }}
            />
            <span className="field-hint">
              {data?.password_set
                ? "Kiritilgan. Bo'sh qoldirsangiz o'zgarmaydi."
                : "Hali kiritilmagan."}
            </span>
          </label>
        </div>

        <div className="dialog-actions" style={{ justifyContent: "flex-start", flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn btn-filled"
            disabled={!canEdit || !data?.encrypted || save.isPending}
            onClick={() => {
              setTested(null);
              save.mutate(undefined, {
                onSuccess: () => {
                  setPassword("");
                  void state.refetch();
                },
              });
            }}
          >
            <Icon name="check" size={16} />
            Saqlash
          </button>
          {/* Sinovsiz noto'g'ri parol faqat birinchi hujjat
              yuborilganda — ish vaqtida — bilinardi. */}
          <button
            type="button"
            className="btn btn-tonal"
            disabled={!canEdit || test.isPending}
            onClick={() =>
              test.mutate(undefined, {
                onSuccess: (result) => setTested(`1C qabul qildi (HTTP ${result.status})`),
              })
            }
          >
            <Icon name="refresh" size={16} />
            Sinab ko'rish
          </button>
        </div>

        {!canEdit && (
          <p className="field-hint">
            O'zgartirish uchun <code>integration.mapping.manage</code> vakolati kerak.
          </p>
        )}
        {save.error && <ErrorState error={save.error} />}
        {test.error && <ErrorState error={test.error} />}
        {tested && (
          <div className="notice">
            <Icon name="check" size={16} />
            <span>{tested}</span>
          </div>
        )}
        {data?.updated_at && (
          <p className="field-hint" title={dateTime(data.updated_at)}>
            Oxirgi o'zgarish {relative(data.updated_at)} (v{data.version}).
          </p>
        )}
      </div>
    </section>
  );
}
